import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Circle, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from 'components/Button/Button';
import { Modal } from 'components/Modal/Modal';
import { TutorialStep } from './TutorialStep';
import { useTutorialStore } from 'stores/useTutorialStore';
import credits1Image from 'assets/tutorial/credits1.png';
import credits2Image from 'assets/tutorial/credits2.png';
import credits3Image from 'assets/tutorial/credits3.png';
import extraDevices1Image from 'assets/tutorial/extra-devices/1.jpg';
import extraDevices2Image from 'assets/tutorial/extra-devices/2.jpg';
import extraDevices3Image from 'assets/tutorial/extra-devices/3.jpg';

const TUTORIAL_SECTIONS = [
  {
    id: 'credits_system',
    title: 'tutorial.credits.title',
    steps: [
      {
        id: 1,
        title: 'tutorial.credits.steps.introducing.title',
        description: 'tutorial.credits.steps.introducing.description',
        image: credits1Image
      },
      {
        id: 2,
        title: 'tutorial.credits.steps.howItWorks.title',
        description: 'tutorial.credits.steps.howItWorks.description',
        image: credits2Image
      },
      {
        id: 3,
        title: 'tutorial.credits.steps.managing.title',
        description: 'tutorial.credits.steps.managing.description',
        image: credits3Image
      },
      {
        id: 4,
        title: 'tutorial.credits.steps.whatsNext.title',
        description: 'tutorial.credits.steps.whatsNext.description',
        image: 'https://placehold.co/600x400/1f2937/ffc815?text=Stay+Tuned'
      }
    ]
  },
  {
    id: 'extra_devices',
    title: 'tutorial.extraDevices.title',
    steps: [
      {
        id: 1,
        title: 'tutorial.extraDevices.steps.introducing.title',
        description: 'tutorial.extraDevices.steps.introducing.description',
        image: extraDevices1Image
      },
      {
        id: 2,
        title: 'tutorial.extraDevices.steps.pricing.title',
        description: 'tutorial.extraDevices.steps.pricing.description',
        image: extraDevices2Image
      },
      {
        id: 3,
        title: 'tutorial.extraDevices.steps.management.title',
        description: 'tutorial.extraDevices.steps.management.description',
        image: extraDevices3Image
      },
      {
        id: 4,
        title: 'tutorial.extraDevices.steps.flexibility.title',
        description: 'tutorial.extraDevices.steps.flexibility.description',
        image: 'https://placehold.co/600x400/black/ffc815?text=Total+Flexibility'
      }
    ]
  }
];

const getFirstIncompleteSection = (sections, progress) => {
  return sections.find(section => !progress[section.id]?.completed) || sections[0];
};

export const Tutorial = ({ onClose }) => {
  const { t } = useTranslation();
  const { progress, markSectionCompleted } = useTutorialStore();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentSection = TUTORIAL_SECTIONS[currentSectionIndex];
  const currentStep = currentSection?.steps[currentStepIndex];

  useEffect(() => {
    const firstIncompleteSection = getFirstIncompleteSection(TUTORIAL_SECTIONS, progress);
    const sectionIndex = TUTORIAL_SECTIONS.findIndex(section => section.id === firstIncompleteSection.id);
    setCurrentSectionIndex(sectionIndex);
    setCurrentStepIndex(0);
  }, [progress]);

  const handleNext = () => {
    if (currentStepIndex === currentSection.steps.length - 1) {
      markSectionCompleted(currentSection.id);

      const nextSectionIndex = currentSectionIndex + 1;
      if (nextSectionIndex < TUTORIAL_SECTIONS.length) {
        setCurrentSectionIndex(nextSectionIndex);
        setCurrentStepIndex(0);
      } else {
        handleClose();
      }
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      const prevSection = TUTORIAL_SECTIONS[currentSectionIndex - 1];
      setCurrentSectionIndex(prev => prev - 1);
      setCurrentStepIndex(prevSection.steps.length - 1);
    }
  };

  const handleSectionSelect = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    setCurrentStepIndex(0);
  };

  const handleClose = onClose;

  const canGoBack = currentStepIndex > 0 || currentSectionIndex > 0;
  const isLastStep = currentSectionIndex === TUTORIAL_SECTIONS.length - 1 &&
                    currentStepIndex === currentSection.steps.length - 1;

  return (
    <Modal
      title={t(currentSection.title)}
      handleCloseModal={handleClose}
      maxWidth="md:w-[860px]"
      bodyClassName="flex min-h-0 flex-col overflow-hidden"
    >
      <div className="flex min-h-0 max-h-[70dvh] flex-col border-t border-ui-hair-2 md:flex-row" data-testid="tutorial-overlay">
        {/* Section list — the same nav-item recipe as Settings: accent text when active on
            an elevated chip, never a dimmer-than-body grey for the inactive ones. */}
        <nav aria-label={t('settings.help.gettingStarted')} className="hidden w-56 flex-none space-y-1 border-r border-ui-hair-2 p-3 md:block">
          {TUTORIAL_SECTIONS.map((section, index) => {
            const isActive = currentSectionIndex === index;
            return (
              <button
                key={section.id}
                type="button"
                aria-current={isActive || undefined}
                onClick={() => handleSectionSelect(index)}
                className={classNames(
                  'flex w-full items-center gap-2.5 rounded-chip px-3 py-2.5 text-left transition-colors duration-150',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
                  isActive ? 'bg-ui-elevated text-ui-accent' : 'text-ui-3 hover:bg-ui-overlay hover:text-ui',
                )}
              >
                {progress[section.id]?.completed
                  ? <CheckCircle size={15} className="flex-none text-ui-success" />
                  : <Circle size={15} className="flex-none text-ui-4" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold">{t(section.title)}</span>
                  <span className="tnum block font-mono text-[11px] text-ui-3">
                    {section.steps.length} {t('tutorial.navigation.steps')}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TutorialStep
              key={`${currentSectionIndex}-${currentStepIndex}`}
              title={t(currentStep.title)}
              description={t(currentStep.description)}
              image={currentStep.image}
            />
          </div>

          <div className="flex-none border-t border-ui-hair-2">
            {/* Sections as numbered chips below md, where the sidebar is hidden. */}
            {TUTORIAL_SECTIONS.length > 1 && (
              <div className="flex items-center justify-center gap-2 border-b border-ui-hair-2 px-4 py-2.5 md:hidden">
                {TUTORIAL_SECTIONS.map((section, index) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleSectionSelect(index)}
                    aria-label={t(section.title)}
                    aria-current={currentSectionIndex === index || undefined}
                    className={classNames(
                      'tnum flex h-7 w-7 items-center justify-center rounded-tile font-mono text-[12px] font-bold transition-colors duration-150',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
                      currentSectionIndex === index
                        ? 'bg-ui-accent text-black'
                        : 'border border-ui-hair-2 bg-ui-sunken text-ui-3 hover:text-ui',
                    )}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 px-4 py-3.5 md:px-5">
              <div className="flex-1">
                {canGoBack && (
                  <Button view="secondary" size="cta" className="rounded-field font-semibold" onClick={handleBack} icon={<ChevronLeft size={15} />}>
                    {t('buttons.back')}
                  </Button>
                )}
              </div>

              {/* Step indicator: dots from md (they need room), a counter below it. */}
              <div className="hidden items-center gap-2 md:flex" role="tablist" aria-label={t('tutorial.navigation.steps')}>
                {currentSection.steps.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={currentStepIndex === index}
                    onClick={() => setCurrentStepIndex(index)}
                    className={classNames(
                      'h-2 rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
                      currentStepIndex === index ? 'w-5 bg-ui-accent' : 'w-2 bg-ui-border hover:bg-ui-border-strong',
                    )}
                    aria-label={`${t('tutorial.navigation.steps')} ${index + 1}`}
                  />
                ))}
              </div>
              <div className="tnum font-mono text-[12px] text-ui-3 md:hidden">
                {currentStepIndex + 1} / {currentSection.steps.length}
              </div>

              <div className="flex flex-1 justify-end">
                <Button view="cta" size="cta" onClick={handleNext}>
                  <span>{isLastStep ? t('buttons.finish') : t('buttons.next')}</span>
                  {!isLastStep && <ChevronRight size={15} className="ml-1.5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
